import { useAppSelector } from '@brands/hooks/useReduxHook';
import { CaseType } from '@brands/services/cases/types/ICase';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { selectStaffCaseCreation } from '@brands/store/selectors/staffCaseCreation';
import { FormValues, UploadedFile } from '@brands/store/slices/patientFormSlice';

const CASE_TYPE_FORM_QUESTION_ID = 20;
const FILE_UPLOAD_FORM_QUESTION_ID = 17;

export const getCaseTypeFromForm = (values: FormValues[]): string | undefined =>
  values.find((item) => item.form_question_id === CASE_TYPE_FORM_QUESTION_ID)?.value.caseType as string | undefined;

// The API's required-unless-file validation runs before the uploaded media can
// be attached to the newly created case. Include the existing upload as an
// intake answer so the API can apply the same rule as the UI.
export const getUploadedFileName = (values: FormValues[]): string | undefined => {
  const images = values.find((item) => item.form_question_id === FILE_UPLOAD_FORM_QUESTION_ID)?.value.images as
    | UploadedFile[]
    | undefined;
  return Array.isArray(images) ? images.find((image) => image.name.trim())?.name.trim() : undefined;
};

export const hasUploadedFile = (values: FormValues[]): boolean => Boolean(getUploadedFileName(values));

/**
 * The medical-history / dental-history questions are optional in two cases:
 * - Clinical Enablement once a photo or file has been attached (DO-3603
 *   acceptance criteria). Photos are collected before the medical-history
 *   steps for this case type specifically so this is knowable by the time
 *   those steps render.
 * - Staff creating a case for a patient who was never invited to the platform
 *   (`patientNotInvited`, see useMedicalHistoryOptional): the client handles
 *   that patient's care in-house and may skip the history for any case type.
 */
export const medicalHistoryOptional = (values: FormValues[], patientNotInvited = false): boolean =>
  patientNotInvited || (getCaseTypeFromForm(values) === CaseType.clinical_enablement && hasUploadedFile(values));

/**
 * `medicalHistoryOptional` for the intake in progress. CreateConsultationFor
 * records the patient when they were never invited; matching on the patient id
 * keeps that from carrying over to a case for someone else.
 */
export const useMedicalHistoryOptional = (values: FormValues[]): boolean => {
  const { notInvitedPatientId } = useAppSelector(selectStaffCaseCreation);
  const { selectedPatient } = useAppSelector(selectPatient);
  const patientNotInvited = notInvitedPatientId != null && Number(selectedPatient?.id) === notInvitedPatientId;
  return medicalHistoryOptional(values, patientNotInvited);
};

// Case types whose medical-history section is followed by the pharmacy step.
const CASE_TYPES_WITH_PHARMACY_STEP: string[] = [
  CaseType.video_call_scheduled,
  CaseType.phone_call_scheduled,
  CaseType.one_report,
  CaseType.second_opinion,
];

/** Where the intake continues once the medical-history section is finished or skipped. */
export const routeAfterClinicalData = (values: FormValues[]): string => {
  const caseType = getCaseTypeFromForm(values);
  if (caseType === CaseType.care_coordination) return '/payment-information';
  return caseType && CASE_TYPES_WITH_PHARMACY_STEP.includes(caseType) ? '/select-pharmacy' : '/describe-pain-level';
};
